/* eslint-disable @typescript-eslint/no-explicit-any */
import Container from "../../components/Container";
import { useQuery } from "@tanstack/react-query";
import Show from "../../components/Show";
import Text from "../../components/Text";
import AssetAccordionHeaderEndContentText from "../../components/AssetAccordionHeaderEndContentText";
import { Skeleton } from "primereact/skeleton";
import Currency from "../../components/Currency";
import PercentageText from "../../components/PercentageText";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import Card from "../../components/Card";
import useAuthHttp from "../../hooks/useAuthHttp";
import { Chart } from "primereact/chart";
import moment from "moment";
import AssetAllocationCardItem, { colors } from "../../components/cards/AssetAllocationCardItem";
import { SelectButton } from "primereact/selectbutton";
import { useCallback, useState } from "react";
import { Accordion, AccordionTab } from "primereact/accordion";
import AccordionHeader from "../../components/AccordionHeader";
import { AssetFactory } from "../../models/asset";
import AnalysisContainer from "./AnalysisContainer";
import { useMediaQuery } from "react-responsive";
import { Badge } from "primereact/badge";
import { Tooltip } from "primereact/tooltip";
import Icon from "../../components/Icon";
import {
    trackAnalysisGroupByChanged,
    useAnalysisViewTracking,
} from "../../analytics";

export interface PortfolioProfitBenchmark {
    index_name: string
    period_reference: string
    period_profit_value?: number
    period_percent_value: number
}

interface IProfitBenchmarkGroup {
    index_name: string;
    group_name: string;
    group_description: string;
    group_total_value: number;
    group_total_value_percentage: number;
}

interface IProfitByAsset {
    asset_name: string | null;
    asset_description: string | null;
    asset_id: string;
    asset_due_date: string;
    asset_purchase_date: string;
    asset_capital_gain_period: number;
    asset_current_value: number;
    asset_return_value: number;
    asset_type: string;
    asset_family: string;
    asset_sub_type: string | null;
    asset_total_earnings_period: number;
    initial_value: number;
    current_value: number;
    profit_percentage: number;
    avg_price_value: number | null;
    portfolio_percentage: number;
    financial_institution: string;
    asset_details: Record<string, any>;
}

interface IProfitByVehicle {
    vehicle_name: string;
    vehicle_description: string;
    vehicle_buy_price: number;
    vehicle_actual_price: number;
    vehicle_profit_percentage: number;
    vehicle_portfolio_percentage: number;
    vehicle_id: string;
}

interface IProfitByProperty {
    property_name: string;
    property_description: string;
    property_buy_price: number;
    property_actual_price: number;
    property_profit_percentage: number;
    property_portfolio_percentage: number;
    property_id: string;
}

interface IAnalysisProfit {
    user_id: string;
    portfolio_total_value: number;
    profit_total_value: number;
    profit_total_value_percentage: number;
    profit_benchmark_list: PortfolioProfitBenchmark[];
    profit_benchmark_group_list: IProfitBenchmarkGroup[];
    profit_by_asset_list: IProfitByAsset[];
    profit_by_vehicle_list: IProfitByVehicle[];
    profit_by_property_list: IProfitByProperty[];
}

const FinancialPerformanceCard = ({ portfolio_profit_benchmark }: { portfolio_profit_benchmark: PortfolioProfitBenchmark[]; }) => {
    const financialResultTable = [
        [
            <Text color="900" fontWeight="bold">Rentabilidade acumulada</Text>,
            ...[
                portfolio_profit_benchmark.find(item => item.index_name == "PROFIT" && item.period_reference == "PERIOD")?.period_profit_value,
                portfolio_profit_benchmark.find(item => item.index_name == "PROFIT" && item.period_reference == "THIS_YEAR")?.period_profit_value,
                portfolio_profit_benchmark.find(item => item.index_name == "PROFIT" && item.period_reference == "LAST_12_MONTHS")?.period_profit_value,
                portfolio_profit_benchmark.find(item => item.index_name == "PROFIT" && item.period_reference == "LAST_24_MONTHS")?.period_profit_value,
                portfolio_profit_benchmark.find(item => item.index_name == "PROFIT" && item.period_reference == "BEGIN")?.period_profit_value
            ].map(value => <Currency value={value ?? 0}>{(value) => <Text className="text-900">{value}</Text>}</Currency>),
        ],
        [
            <Text color="900" fontWeight="bold">Rentabilidade</Text>,
            ...[
                portfolio_profit_benchmark.find(item => item.index_name == "PROFIT" && item.period_reference == "PERIOD")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "PROFIT" && item.period_reference == "THIS_YEAR")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "PROFIT" && item.period_reference == "LAST_12_MONTHS")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "PROFIT" && item.period_reference == "LAST_24_MONTHS")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "PROFIT" && item.period_reference == "BEGIN")?.period_percent_value
            ].map(value => <PercentageText>{value ?? 0}</PercentageText>)
        ],
        [
            <Text color="900" fontWeight="bold">% do CDI</Text>,
            ...[
                portfolio_profit_benchmark.find(item => item.index_name == "PERCENT_CDI" && item.period_reference == "PERIOD")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "PERCENT_CDI" && item.period_reference == "THIS_YEAR")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "PERCENT_CDI" && item.period_reference == "LAST_12_MONTHS")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "PERCENT_CDI" && item.period_reference == "LAST_24_MONTHS")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "PERCENT_CDI" && item.period_reference == "BEGIN")?.period_percent_value
            ].map(value => <PercentageText>{value ?? 0}</PercentageText>)
        ],
        [
            <Text color="900" fontWeight="bold">CDI</Text>,
            ...[
                portfolio_profit_benchmark.find(item => item.index_name == "CDI" && item.period_reference == "PERIOD")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "CDI" && item.period_reference == "THIS_YEAR")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "CDI" && item.period_reference == "LAST_12_MONTHS")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "CDI" && item.period_reference == "LAST_24_MONTHS")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "CDI" && item.period_reference == "BEGIN")?.period_percent_value
            ].map(value => <PercentageText>{value ?? 0}</PercentageText>)
        ],
        [
            <Text color="900" fontWeight="bold">IPCA</Text>,
            ...[
                portfolio_profit_benchmark.find(item => item.index_name == "IPCA" && item.period_reference == "PERIOD")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "IPCA" && item.period_reference == "THIS_YEAR")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "IPCA" && item.period_reference == "LAST_12_MONTHS")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "IPCA" && item.period_reference == "LAST_24_MONTHS")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "IPCA" && item.period_reference == "BEGIN")?.period_percent_value
            ].map(value => <PercentageText>{value ?? 0}</PercentageText>)
        ],
        [
            <Text color="900" fontWeight="bold">IGPM</Text>,
            ...[
                portfolio_profit_benchmark.find(item => item.index_name == "IGPM" && item.period_reference == "PERIOD")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "IGPM" && item.period_reference == "THIS_YEAR")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "IGPM" && item.period_reference == "LAST_12_MONTHS")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "IGPM" && item.period_reference == "LAST_24_MONTHS")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "IGPM" && item.period_reference == "BEGIN")?.period_percent_value
            ].map(value => <PercentageText>{value ?? 0}</PercentageText>)
        ],
        [
            <Text color="900" fontWeight="bold">SELIC</Text>,
            ...[
                portfolio_profit_benchmark.find(item => item.index_name == "SELIC" && item.period_reference == "PERIOD")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "SELIC" && item.period_reference == "THIS_YEAR")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "SELIC" && item.period_reference == "LAST_12_MONTHS")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "SELIC" && item.period_reference == "LAST_24_MONTHS")?.period_percent_value,
                portfolio_profit_benchmark.find(item => item.index_name == "SELIC" && item.period_reference == "BEGIN")?.period_percent_value
            ].map(value => <PercentageText>{value ?? 0}</PercentageText>)
        ]
    ];

    return (
        <Card
            title="Rentabilidade"
            subTitle="Rentabilidade acumulada comparativa com benchmarks"
            className="w-full"
        >
            <Container className="table-responsive w-full">
                <DataTable
                    lazy={false}
                    resizableColumns
                    scrollable
                    value={financialResultTable}
                    className="w-full"
                >
                    <Column field="0" header="" frozen alignFrozen="left" />
                    <Column field="1" header="Período" />
                    <Column field="2" header="Ano Atual" />
                    <Column field="3" header="Últimos 12 Meses" />
                    <Column field="4" header="Últimos 24 Meses" />
                    <Column field="5" header="Desde o Início" />
                </DataTable>
            </Container>
        </Card>
    )
}

const ComparativeRentabilityChart = ({ portfolio_profit_benchmark }: { portfolio_profit_benchmark: PortfolioProfitBenchmark[]; }) => {
    const profits = portfolio_profit_benchmark.filter(item => item.index_name == "PROFIT");
    const cdis = portfolio_profit_benchmark.filter(item => item.index_name == "CDI");
    const ipcas = portfolio_profit_benchmark.filter(item => item.index_name == "IPCA");
    const selics = portfolio_profit_benchmark.filter(item => item.index_name == "SELIC");
    const igpms = portfolio_profit_benchmark.filter(item => item.index_name == "IGPM");
    
    return (
        <Card
            title="Rentabilidade comparativa"
            subTitle="Rentabilidade comparativa com índices de referência do mercado"
            className="w-full"
        >
            <Container flexColumn fullWidth>
                <Chart
                    type="line"
                    data={{
                        labels: profits.map(item => {
                            switch (item.period_reference) {
                                case "PERIOD":
                                    return "Período";
                                case "THIS_YEAR":
                                    return "Ano Atual";
                                case "LAST_12_MONTHS":
                                    return "Últimos 12 Meses";
                                case "LAST_24_MONTHS":
                                    return "Últimos 24 Meses";
                                case "BEGIN":
                                    return "Desde o Início";
                                default:
                                    return item.period_reference;
                            }
                        }),
                        datasets: [
                            {
                                label: "Carteira",
                                data: profits.map(item => item.period_percent_value),
                                color: "#42A5F5",
                                tension: 0.4
                            },
                            {
                                label: "CDI",
                                data: cdis.map(item => item.period_percent_value),
                                color: "#66BB6A",
                                tension: 0.4
                            },
                            {
                                label: "IPCA",
                                data: ipcas.map(item => item.period_percent_value),
                                color: "#FFA726",
                                tension: 0.4
                            },
                            {
                                label: "SELIC",
                                data: selics.map(item => item.period_percent_value),
                                color: "#EF5350",
                                tension: 0.4
                            },
                            {
                                label: "IGPM",
                                data: igpms.map(item => item.period_percent_value),
                                color: "#AB47BC",
                                tension: 0.4
                            }
                        ]
                    }}
                />
            </Container>
        </Card>
    )
}

const ProfitAnalysisGrouped = ({ profit_benchmark_group_list }: { profit_benchmark_group_list: IProfitBenchmarkGroup[] }) => {
    const [selectedGroup, setSelectedGroup] = useState<string>("");
    const authHttp = useAuthHttp();
    const rentabilityQuery = useQuery({
        queryKey: ["rentability", selectedGroup],
        queryFn: async () => {
            if (!selectedGroup) {
                return profit_benchmark_group_list;
            }

            const response = await authHttp.doGet<{ analysis_profit: IAnalysisProfit }>(
                `analysis/profit${selectedGroup ? `?group_by=${selectedGroup}` : ''}`
            );

            return response.analysis_profit?.profit_benchmark_group_list ?? [];
        }
    });

    const getCards = useCallback(() => {
        const group = [
            {
                id: "fixed_income",
                label: "Renda Fixa"
            },
            {
                id: "variable_income",
                label: "Renda variável"
            },
            {
                id: "cash",
                label: "Em caixa"
            },
            {
                id: "vehicle",
                label: "Veículos"
            },
            {
                id: "property",
                label: "Imóveis"
            }
        ];

        const assetTypes = [
            {
                id: "PB",
                label: "Títulos privados",
                color: "bg-fixed-income"
            },
            {
                id: "CS",
                label: "Personalizados",
                color: "bg-fixed-income"
            },
            {
                id: "SA",
                label: "Poupança",
                color: "bg-fixed-income"
            },
            {
                id: "DB",
                label: "Debêntures",
                color: "bg-fixed-income"
            },
            {
                id: "TB",
                label: "Títulos públicos",
                color: "bg-fixed-income"
            },
            {
                id: "CY",
                label: "Moedas",
                color: "bg-variable-income"
            },
            {
                id: "CM",
                label: "Criptomoedas",
                color: "bg-variable-income"
            },
            {
                id: "IF",
                label: "Fundos de investimento",
                color: "bg-variable-income"
            },
            {
                id: "ST",
                label: "Ações",
                color: "bg-variable-income"
            },
            {
                id: "CA",
                label: "Conta Corrente",
                color: "bg-cashflow-income"
            },
            {
                id: "vehicle",
                label: "Veículos",
                color: "bg-vehicles"
            },
            {
                id: "property",
                label: "Imóveis",
                color: "bg-properties"
            }
        ];

        switch (selectedGroup) {
            case "asset_type":
                return assetTypes.map((g) => {
                    const group = rentabilityQuery.data?.find(p => p.group_name == g.id);
                    return {
                        name: g.label,
                        color: g.color,
                        percentage: (group?.group_total_value_percentage ?? 0) * 100,
                        value: group?.group_total_value ?? 0,
                    }
                });
            case "fin_issue":
                return rentabilityQuery.data?.map((g) => {
                    return {
                        name: g.group_description ? g.group_description.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') : "Sem instituição atrelada",
                        color: colors[0],
                        percentage: g.group_total_value_percentage * 100,
                        value: g.group_total_value,
                    }
                });
            default:
                return group.map((g, index) => {
                    const group = rentabilityQuery.data?.find(p => p.group_name == g.id);
                    return {
                        name: g.label,
                        color: colors[index],
                        percentage: (group?.group_total_value_percentage ?? 0) * 100,
                        value: group?.group_total_value ?? 0,
                    }
                });
        }
    }, [rentabilityQuery.data, selectedGroup]);

    return (
        <Card
            title="Rentabilidade Agrupada"
            subTitle="Rentabilidade percentual por agrupamento"
            className="w-full"
            action={
                <Container>
                    <SelectButton
                        multiple={false}
                        value={selectedGroup}
                        onChange={(e) => {
                            const groupBy = e.value === '' ? 'group' : e.value === 'asset_type' ? 'subgroup' : 'institution';
                            trackAnalysisGroupByChanged('rentability', groupBy);
                            setSelectedGroup(e.value);
                        }}
                        options={[
                            { label: "Grupo", value: "" },
                            { label: "Subgrupo", value: "asset_type" },
                            { label: "Instituição", value: "fin_issue" }
                        ]}
                    />
                </Container>
            }
        >
            <Show
                when={!rentabilityQuery.isLoading}
                fallback={
                    <Container wrap fullWidth gap={3} flexColumn>
                        <Container wrap fullWidth gap={3}>
                            <Skeleton height="90px" className="flex border-1 bg-gray-200 border-gray-300 flex-1" />
                            <Skeleton height="90px" className="flex border-1 bg-gray-200 border-gray-300 flex-1" />
                            <Skeleton height="90px" className="flex border-1 bg-gray-200 border-gray-300 flex-1" />
                        </Container>

                        <Container wrap fullWidth gap={3}>
                            <Skeleton height="90px" className="flex border-1 bg-gray-200 border-gray-300 flex-1" />
                            <Skeleton height="90px" className="flex border-1 bg-gray-200 border-gray-300 flex-1" />
                            <Skeleton height="90px" className="flex border-1 bg-gray-200 border-gray-300 flex-1" />
                        </Container>
                    </Container>
                }
            >
                <Container wrap fullWidth gap={3}>
                    {
                        getCards()
                            ?.sort((a, b) => b.percentage - a.percentage)
                            ?.map(card => <AssetAllocationCardItem key={card.name} {...card} />)
                    }
                </Container>
            </Show>
        </Card>
    )
}

interface IAssetAccordionHeaderEndContentProps {
    investedValue?: number;
    currentValue?: number;
    returnValue?: number;
    rentability?: number;
    walletPercentage?: number;
}

const AssetAccordionHeaderEndContent = ({
    investedValue,
    currentValue,
    returnValue,
    rentability,
    walletPercentage
}: IAssetAccordionHeaderEndContentProps) => {
    return (
        <Container wrapResponsive gap={3}>
            <Show when={!!investedValue}>
                <AssetAccordionHeaderEndContentText
                    title="Valor investido"
                    value={investedValue ?? 0}
                    isCurrency
                />
            </Show>

            <Show when={!!currentValue}>
                <AssetAccordionHeaderEndContentText
                    title="Valor atual"
                    value={currentValue ?? 0}
                    isCurrency
                />
            </Show>

            <Show when={!!returnValue}>
                <AssetAccordionHeaderEndContentText
                    title="Valor com proventos"
                    value={returnValue ?? 0}
                    isCurrency
                />
            </Show>

            <Show when={!!rentability}>
                <AssetAccordionHeaderEndContentText
                    title="Rentabilidade"
                    value={rentability ?? 0}
                    isPercentage
                />
            </Show>

            <Show when={!!walletPercentage}>
                <AssetAccordionHeaderEndContentText
                    title="% da carteira"
                    value={walletPercentage ?? 0}
                    isPercentage
                />
            </Show>
        </Container>
    )
}

const ProductRentabilityTable = ({ 
    profit_by_asset_list, 
    profit_by_vehicle_list, 
    profit_by_property_list 
}: { 
    profit_by_asset_list: IProfitByAsset[]; 
    profit_by_vehicle_list: IProfitByVehicle[];
    profit_by_property_list: IProfitByProperty[]; 
}) => {
    const isTablet = useMediaQuery({ minWidth: 569, maxWidth: 768 });
    const isMobile = useMediaQuery({ maxWidth: 568 });
    
    const profit_by_asset_list_mapped = profit_by_asset_list.map(item => {
        return {
            ...item,
            profit_percentage: item.profit_percentage * 100,
            portfolio_percentage: item.portfolio_percentage * 100
        }
    });
    const fixedIncomeList = profit_by_asset_list_mapped
        .filter(item => item.asset_family == "F")
        .sort((a, b) => b.initial_value - a.initial_value);
    const variableIncomeList = profit_by_asset_list_mapped
        .filter(item => item.asset_family == "V" || item.asset_family == "T" || item.asset_family == "U")
        .sort((a, b) => b.initial_value - a.initial_value);
    const cashList = profit_by_asset_list_mapped
        .filter(item => item.asset_family == "C")
        .sort((a, b) => b.initial_value - a.initial_value);

    return (
        <Card
            title="Rentabilidade por produto"
            subTitle="Rentabilidade por produto e grupo de ativos"
            className="w-full"
        >
            <Container className="p-accordion-fixed-income" fullWidth>
                <Accordion>
                    <AccordionTab
                        header={
                            <AccordionHeader
                                title="Renda Fixa"
                                subTitle={`${fixedIncomeList.length} ativos`}
                                endContent={
                                    <AssetAccordionHeaderEndContent
                                        investedValue={fixedIncomeList.reduce((acc, item) => acc + item.initial_value, 0)}
                                        returnValue={fixedIncomeList.reduce((acc, item) => acc + item.asset_return_value, 0)}
                                        rentability={fixedIncomeList.reduce((acc, item) => acc + item.profit_percentage, 0)}
                                    />
                                }
                            />
                        }
                    >
                        <DataTable
                            value={fixedIncomeList}
                            resizableColumns
                        >
                            <Column
                                field="asset_name"
                                frozen={!isTablet && !isMobile}
                                alignFrozen="left"
                                header="Nome"
                                body={(item) => {
                                    return (
                                        <Text color={moment(item.asset_due_date).isSameOrBefore(moment()) ? "red-500" : "700"} fontWeight="normal" size="base">
                                            {AssetFactory.getNameByMainType(item.asset_type, item)}
                                        </Text>
                                    )
                                }}
                            />
                            <Column
                                field="asset_purchase_date"
                                header="Compra"
                                sortable
                                body={(item) => {
                                    return (
                                        <Text color={moment(item.asset_due_date).isSameOrBefore(moment()) ? "red-500" : "700"} fontWeight="normal" size="base">
                                            {item.asset_purchase_date ? moment(item.asset_purchase_date).format("DD/MM/YYYY") : "-"}
                                        </Text>
                                    )
                                }}
                            />
                            <Column
                                field="asset_due_date"
                                header="Vencimento"
                                sortable
                                body={(item) => {
                                    return (
                                        <Text color={moment(item.asset_due_date).isSameOrBefore(moment()) ? "red-500" : "700"} fontWeight="normal" size="base">
                                            {item.asset_due_date ? moment(item.asset_due_date).format("DD/MM/YYYY") : "-"}
                                        </Text>
                                    )
                                }}
                            />
                            <Column
                                field="initial_value"
                                header="Valor investido"
                                sortable
                                body={(item) => {
                                    return (
                                        <Currency value={item.initial_value}>
                                            {(value) => (
                                                <Text color={moment(item.asset_due_date).isSameOrBefore(moment()) ? "red-500" : "700"} fontWeight="normal" size="base">{value}</Text>
                                            )}
                                        </Currency>
                                    )
                                }}
                            />
                            <Column
                                field="asset_current_value"
                                header="Valor atual"
                                sortable
                                body={(item) => {
                                    return (
                                        <Currency value={item.asset_current_value}>
                                            {(value) => (
                                                <Text color={moment(item.asset_due_date).isSameOrBefore(moment()) ? "red-500" : "700"} fontWeight="normal" size="base">{value}</Text>
                                            )}
                                        </Currency>
                                    )
                                }}
                            />
                            <Column
                                header="Proventos recebidos"
                                sortable
                                body={(item) => {
                                    return (
                                        <Currency value={item.asset_total_earnings_period}>
                                            {(value) => (
                                                <Text color={moment(item.asset_due_date).isSameOrBefore(moment()) ? "red-500" : "700"} fontWeight="normal" size="base">{value}</Text>
                                            )}
                                        </Currency>
                                    )
                                }}
                            />
                            <Column
                                header="Rentabilidade acumulada"
                                sortable
                                body={(item) => {
                                    return (
                                        <Currency value={item.asset_rentability_period}>
                                            {(value) => (
                                                <Text color="700" fontWeight="normal" size="base">{value}</Text>
                                            )}
                                        </Currency>
                                    )
                                }}
                            />
                            <Column
                                field="asset_return_value"
                                header="Valor acumulado"
                                sortable
                                body={(item) => {
                                    return (
                                        <Currency value={item.asset_return_value}>
                                            {(value) => (
                                                <Text color={moment(item.asset_due_date).isSameOrBefore(moment()) ? "red-500" : "700"} fontWeight="normal" size="base">{value}</Text>
                                            )}
                                        </Currency>
                                    )
                                }}
                            />
                            <Column
                                field="profit_percentage"
                                header="Rentabilidade"
                                sortable
                                body={(item) => {
                                    return (
                                        <PercentageText color={moment(item.asset_due_date).isSameOrBefore(moment()) ? "red-500" : "700"} fontWeight="normal" size="base">{item.profit_percentage}</PercentageText>
                                    )
                                }}
                            />
                            <Column
                                field="financial_institution"
                                header="Custodiante"
                                body={(item) => {
                                    return (
                                        <Text color={moment(item.asset_due_date).isSameOrBefore(moment()) ? "red-500" : "700"} fontWeight="normal" size="base">{item.financial_institution}</Text>
                                    )
                                }}
                            />
                        </DataTable>
                    </AccordionTab>
                </Accordion>
            </Container>

            <Container className="p-accordion-variable-income" fullWidth>
                <Accordion>
                    <AccordionTab
                        header={
                            <AccordionHeader
                                title="Renda variável"
                                subTitle={`${variableIncomeList.length} ativos`}
                                endContent={
                                    <AssetAccordionHeaderEndContent
                                        investedValue={variableIncomeList.reduce((acc, item) => acc + item.initial_value, 0)}
                                        returnValue={variableIncomeList.reduce((acc, item) => acc + item.asset_return_value, 0)}
                                        rentability={variableIncomeList.reduce((acc, item) => acc + item.profit_percentage, 0)}
                                    />
                                }
                            />
                        }
                    >
                        <DataTable
                            value={variableIncomeList}
                            resizableColumns
                        >
                            <Column
                                field="asset_name"
                                header="Nome"
                                frozen={!isTablet && !isMobile}
                                alignFrozen="left"
                                body={(item) => {
                                    return AssetFactory.getNameByMainType(item.asset_type, item);
                                }}
                            />
                            <Column
                                field="initial_value"
                                header="Valor investido"
                                sortable
                                body={(item) => {
                                    return (
                                        <Currency value={item.initial_value}>
                                            {(value) => (
                                                <Text color="700" fontWeight="normal" size="base">{value}</Text>
                                            )}
                                        </Currency>
                                    )
                                }}
                            />
                            <Column
                                header="Quantidade comprada"
                                sortable
                                body={(item) => {
                                    return (
                                        <Text color="700" fontWeight="normal" size="base">
                                            {
                                                (item.asset_details.if_quantity_actual_amount ?? item.asset_details.if_qtd_prch) 
                                                ?? (item.asset_details.st_quantity_actual_amount ?? item.asset_details.st_qtd_prch) 
                                                ?? (item.asset_details.cy_quantity_actual_amount ?? item.asset_details.cy_quantity_prch)
                                                ?? (item.asset_details.cm_quantity_actual_amount ?? item.asset_details.cm_quantity_prch) 
                                                ?? "-"
                                            }
                                        </Text>
                                    )
                                }}
                            />
                            <Column
                                header="Rentabilidade acumulada"
                                sortable
                                body={(item) => {
                                    return (
                                        <Currency value={item.asset_rentability_period}>
                                            {(value) => (
                                                <Text color="700" fontWeight="normal" size="base">{value}</Text>
                                            )}
                                        </Currency>
                                    )
                                }}
                            />
                            <Column
                                field="asset_return_value"
                                header="Valor acumulado"
                                sortable
                                body={(item) => {
                                    return (
                                        <Currency value={item.asset_return_value}>
                                            {(value) => (
                                                <Text color="700" fontWeight="normal" size="base">{value}</Text>
                                            )}
                                        </Currency>
                                    )
                                }}
                            />
                            <Column
                                field="asset_current_value"
                                header="Valor atual"
                                sortable
                                body={(item) => {
                                    return (
                                        <Currency value={item.asset_current_value}>
                                            {(value) => (
                                                <Text color="700" fontWeight="normal" size="base">{value}</Text>
                                            )}
                                        </Currency>
                                    )
                                }}
                            />
                            <Column
                                header="Proventos recebidos"
                                sortable
                                body={(item) => {
                                    return (
                                        <Currency value={item.asset_total_earnings_period}>
                                            {(value) => (
                                                <Text color="700" fontWeight="normal" size="base">{value}</Text>
                                            )}
                                        </Currency>
                                    )
                                }}
                            />
                            <Column
                                field="avg_price_value"
                                header="Preço médio"
                                sortable
                                body={(item) => {
                                    return (
                                        <Currency value={item.avg_price_value}>
                                            {(value) => (
                                                <Text color="700" fontWeight="normal" size="base">{value}</Text>
                                            )}
                                        </Currency>
                                    )
                                }}
                            />
                            <Column
                                field="profit_percentage"
                                header="Rentabilidade"
                                sortable
                                body={(item) => {
                                    return (
                                        <PercentageText color="700" fontWeight="normal" size="base">{item.profit_percentage}</PercentageText>
                                    )
                                }}
                            />
                            <Column
                                field="financial_institution"
                                header="Custodiante"
                                body={(item) => {
                                    return (
                                        <Text color="700" fontWeight="normal" size="base">{item.financial_institution}</Text>
                                    )
                                }}
                            />
                        </DataTable>
                    </AccordionTab>
                </Accordion>
            </Container>

            <Container className="p-accordion-cashflow" fullWidth>
                <Accordion>
                    <AccordionTab
                        header={
                            <AccordionHeader
                                title="Em caixa"
                                subTitle={`${cashList.length} ativos`}
                                endContent={
                                    <AssetAccordionHeaderEndContent
                                        investedValue={cashList.reduce((acc, item) => acc + item.initial_value, 0)}
                                        returnValue={cashList.reduce((acc, item) => acc + item.asset_return_value, 0)}
                                        rentability={cashList.reduce((acc, item) => acc + item.profit_percentage, 0)}
                                    />
                                }
                            />
                        }
                    >
                        <DataTable
                            value={cashList}
                            resizableColumns
                        >
                            <Column
                                field="asset_name"
                                header="Nome"
                                frozen={!isTablet && !isMobile}
                                alignFrozen="left"
                                body={(item) => {
                                    return item.asset_name ?? item.asset_description ?? "-";
                                }}
                            />
                            <Column
                                field="initial_value"
                                header="Valor investido"
                                sortable
                                body={(item) => {
                                    return (
                                        <Currency value={item.initial_value}>
                                            {(value) => (
                                                <Text color="700" fontWeight="normal" size="base">{value}</Text>
                                            )}
                                        </Currency>
                                    )
                                }}
                            />
                            <Column
                                field="current_value"
                                header="Valor atual"
                                sortable
                                body={(item) => {
                                    return (
                                        <Currency value={item.current_value}>
                                            {(value) => (
                                                <Text color="700" fontWeight="normal" size="base">{value}</Text>
                                            )}
                                        </Currency>
                                    )
                                }}
                            />
                            <Column
                                field="profit_percentage"
                                header="Rentabilidade"
                                sortable
                                body={(item) => {
                                    return (
                                        <PercentageText color="700" fontWeight="normal" size="base">{item.profit_percentage}</PercentageText>
                                    )
                                }}
                            />
                            <Column
                                header="Rentabilidade acumulada"
                                sortable
                                body={(item) => {
                                    return (
                                        <Currency value={item.asset_rentability_period}>
                                            {(value) => (
                                                <Text color="700" fontWeight="normal" size="base">{value}</Text>
                                            )}
                                        </Currency>
                                    )
                                }}
                            />
                            <Column
                                field="financial_institution"
                                header="Custodiante"
                                body={(item) => {
                                    return (
                                        <Text color="700" fontWeight="normal" size="base">{item.financial_institution}</Text>
                                    )
                                }}
                            />
                        </DataTable>
                    </AccordionTab>
                </Accordion>
            </Container>

            <Container className="p-accordion-vehicles" fullWidth>
                <Accordion>
                    <AccordionTab
                        header={
                            <AccordionHeader
                                title="Veículos"
                                subTitle={`${profit_by_vehicle_list.length} ativos`}
                                endContent={
                                    <AssetAccordionHeaderEndContent
                                        investedValue={profit_by_vehicle_list.reduce((acc, item) => acc + item.vehicle_buy_price, 0)}
                                        currentValue={profit_by_vehicle_list.reduce((acc, item) => acc + item.vehicle_actual_price, 0)}
                                        rentability={profit_by_vehicle_list.reduce((acc, item) => acc + item.vehicle_profit_percentage, 0)}
                                    />
                                }
                            />
                        }
                    >
                        <DataTable
                            value={profit_by_vehicle_list}
                            resizableColumns
                        >
                            <Column
                                field="vehicle_name"
                                header="Nome"
                                frozen={!isTablet && !isMobile}
                                alignFrozen="left"
                                body={(item) => {
                                    return item.vehicle_name ?? item.vehicle_description ?? "-";
                                }}
                            />
                            <Column
                                field="vehicle_status"
                                header="Status"
                                sortable
                                body={(item) => {
                                    return <Badge value={item.vehicle_status == 1 ? "Ativo" : "Vendido"} severity={item.vehicle_status == 1 ? "success" : "warning"} />;
                                }}
                            />
                            <Column
                                field="vehicle_buy_price"
                                header="Valor investido"
                                sortable
                                body={(item) => {
                                    return (
                                        <Currency value={item.vehicle_buy_price}>
                                            {(value) => (
                                                <Text color="700" fontWeight="normal" size="base">{value}</Text>
                                            )}
                                        </Currency>
                                    )
                                }}
                            />
                            <Column
                                field="vehicle_actual_price"
                                header="Valor atual"
                                sortable
                                body={(item) => {
                                    return (
                                        <Currency value={item.vehicle_actual_price}>
                                            {(value) => (
                                                <Text color="700" fontWeight="normal" size="base">{value}</Text>
                                            )}
                                        </Currency>
                                    )
                                }}
                            />
                            <Column
                                field="vehicle_profit_percentage"
                                header="Rentabilidade"
                                sortable
                                body={(item) => {
                                    return (
                                        <PercentageText color="700" fontWeight="normal" size="base">{item.vehicle_profit_percentage}</PercentageText>
                                    )
                                }}
                            />
                            <Column
                                header="Rentabilidade acumulada"
                                sortable
                                body={(item) => {
                                    return (
                                        <Currency value={item.vehicle_rentability_period ?? 0}>
                                            {(value) => (
                                                <Text color="700" fontWeight="normal" size="base">{value}</Text>
                                            )}
                                        </Currency>
                                    )
                                }}
                            />
                        </DataTable>
                    </AccordionTab>
                </Accordion>
            </Container>

            <Container className="p-accordion-properties" fullWidth>
                <Accordion>
                    <AccordionTab
                        header={
                            <AccordionHeader
                                title="Imóveis"
                                subTitle={`${profit_by_property_list.length} ativos`}
                                endContent={
                                    <AssetAccordionHeaderEndContent
                                        investedValue={profit_by_property_list.reduce((acc, item) => acc + item.property_buy_price, 0)}
                                        currentValue={profit_by_property_list.reduce((acc, item) => acc + item.property_actual_price, 0)}
                                        rentability={profit_by_property_list.reduce((acc, item) => acc + item.property_profit_percentage, 0)}
                                    />
                                }
                            />
                        }
                    >
                        <DataTable
                            value={profit_by_property_list}
                            resizableColumns
                        >
                            <Column
                                field="property_name"
                                header="Nome"
                                frozen={!isTablet && !isMobile}
                                alignFrozen="left"
                                body={(item) => {
                                    return item.property_name ?? item.property_description ?? "-";
                                }}
                            />
                            <Column
                                field="property_status"
                                header="Status"
                                sortable
                                body={(item) => {
                                    return <Badge value={item.property_status == 1 ? "Ativo" : "Vendido"} severity={item.property_status == 1 ? "success" : "warning"} />;
                                }}
                            />
                            <Column
                                field="property_buy_price"
                                header="Valor investido"
                                sortable
                                body={(item) => {
                                    return (
                                        <Currency value={item.property_buy_price}>
                                            {(value) => (
                                                <Text color="700" fontWeight="normal" size="base">{value}</Text>
                                            )}
                                        </Currency>
                                    )
                                }}
                            />
                            <Column
                                field="property_actual_price"
                                header="Valor atual"
                                sortable
                                body={(item) => {
                                    return (
                                        <Currency value={item.property_actual_price}>
                                            {(value) => (
                                                <Text color="700" fontWeight="normal" size="base">{value}</Text>
                                            )}
                                        </Currency>
                                    )
                                }}
                            />
                            <Column
                                field="property_profit_percentage"
                                header="Rentabilidade"
                                sortable
                                body={(item) => {
                                    return (
                                        <PercentageText color="700" fontWeight="normal" size="base">{item.property_profit_percentage}</PercentageText>
                                    )
                                }}
                            />
                            <Column
                                header="Rentabilidade acumulada"
                                sortable
                                body={(item) => {
                                    return (
                                        <Currency value={item.vehicle_rentability_period}>
                                            {(value) => (
                                                <Text color="700" fontWeight="normal" size="base">{value}</Text>
                                            )}
                                        </Currency>
                                    )
                                }}
                            />
                        </DataTable>
                    </AccordionTab>
                </Accordion>
            </Container>
        </Card>
    )
}

export default function RentabilityPage() {
    const authHttp = useAuthHttp();
    const [period, setPeriod] = useState<Date[]>([]);
    const rentabilityQuery = useQuery({
        queryKey: [
            "rentability",
            period.length == 2 ? moment(period[0]).format("YYYY-MM-DD") : "",
            period.length == 2 ? moment(period[1]).format("YYYY-MM-DD") : "",
        ],
        queryFn: async () => {
            const searchParams = new URLSearchParams();
            if (period.length == 2) {
                searchParams.append("start_date", moment(period[0]).format("YYYY-MM-DD"));
                searchParams.append("end_date", moment(period[1]).format("YYYY-MM-DD"));
            }

            const response = await authHttp.doGet<{ analysis_profit: IAnalysisProfit }>(`analysis/profit?${searchParams.toString()}`);

            return response;
        }
    });

    useAnalysisViewTracking('rentability', rentabilityQuery.isSuccess);

    return (
        <AnalysisContainer
            loading={rentabilityQuery.isLoading}
            period={period}
            setPeriod={setPeriod}
            analysisType="rentability"
            showExport={{ filename: "rentabilidade" }}
        >
            <Container wrap flexColumn center fullWidth gap={3} className="flex items-center">
                <Container wrap gap={3} fullWidth>
                    <Container className="flex flex-1 bg-white border-1 border-gray-300 gap-2 p-3 border-round justify-center items-center" flexColumn>
                        <Tooltip content="Rentabilidade acumulada no período" target=".rentability-total-value" position="left" />
                        <Text className="text-sm text-900 flex align-items-center">
                            Rentabilidade acumulada no período
                            <Icon name="pi pi-info-circle" className="ml-2 rentability-total-value" />
                        </Text>
                        <Currency value={rentabilityQuery.data?.analysis_profit.profit_total_value ?? 0}>
                            {(value) => (
                                <Text className="font-bold text-900 text-lg">{value}</Text>
                            )}
                        </Currency>
                    </Container>
                </Container>

                <ComparativeRentabilityChart
                    portfolio_profit_benchmark={(rentabilityQuery.data?.analysis_profit.profit_benchmark_list ?? []) as PortfolioProfitBenchmark[]}
                />

                <FinancialPerformanceCard
                    portfolio_profit_benchmark={(rentabilityQuery.data?.analysis_profit.profit_benchmark_list ?? []) as PortfolioProfitBenchmark[]}
                />

                <ProfitAnalysisGrouped
                    profit_benchmark_group_list={(rentabilityQuery.data?.analysis_profit.profit_benchmark_group_list ?? [])}
                />

                <ProductRentabilityTable
                    profit_by_asset_list={(rentabilityQuery.data?.analysis_profit.profit_by_asset_list ?? [])}
                    profit_by_vehicle_list={(rentabilityQuery.data?.analysis_profit.profit_by_vehicle_list ?? [])
                        .map(item => ({ ...item, vehicle_portfolio_percentage: item.vehicle_portfolio_percentage * 100, vehicle_profit_percentage: item.vehicle_profit_percentage * 100 }))}
                    profit_by_property_list={(rentabilityQuery.data?.analysis_profit.profit_by_property_list ?? [])
                        .map(item => ({ ...item, property_portfolio_percentage: item.property_portfolio_percentage * 100, property_profit_percentage: item.property_profit_percentage * 100 }))}
                />
            </Container>
        </AnalysisContainer>
    )
}
